import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import logo from '../../assets/logo-getit.png';
import logoPosgo from '../../assets/logo-posgo.png';
import { useCombos } from '../../context/CombosContext';

const PROXY_URL = 'http://localhost:9000';

export const bannerCache = new Map();

const ProductOptionsModal = ({ product, isCombo, onClose, onSubmit }) => {
    const [selectedQuantities, setSelectedQuantities] = useState({});
    const [expandedSections, setExpandedSections] = useState({});
    const [categoryErrors, setCategoryErrors] = useState({});

    // Combo / drink picker state
    const { getComboGroups, hasComboGroupsSync } = useCombos();
    const [comboGroups, setComboGroups] = useState([]);
    // { combo_line_id, combo_price, drink_group: { ... } }
    const [selectedComboGroup, setSelectedComboGroup] = useState(null);
    // { comboLineId, combo_price, drink: { id, name, price, image_base64, custom_attributes, category_limits } }
    const [selectedDrink, setSelectedDrink] = useState(null);
    // atributos de la bebida seleccionada: { [attrId]: qty }
    const [drinkAttributeQuantities, setDrinkAttributeQuantities] = useState({});
    const [drinkAttrExpanded, setDrinkAttrExpanded] = useState({});
    const [drinkCategoryErrors, setDrinkCategoryErrors] = useState({});
    // 'loading' | 'combo' | 'detail'
    const [step, setStep] = useState(() => {
        if (isCombo) return 'detail';
        const cached = hasComboGroupsSync(product.id);
        if (cached === true) return 'combo';
        if (cached === false) return 'detail';
        return 'loading';
    });

    const getInitialBannerSrc = () => {
        if (isCombo && product.comboProducts?.length > 0) {
            const first = product.comboProducts[0]?.product_id || product.comboProducts[0]?.id;
            const second = product.comboProducts[1]?.product_id || product.comboProducts[1]?.id;
            if (first && bannerCache.has(first)) return bannerCache.get(first);
            if (second && bannerCache.has(second)) return bannerCache.get(second);
            return null;
        }
        return bannerCache.get(product.id) || null;
    };

    const [bannerSrc, setBannerSrc] = useState(getInitialBannerSrc);

    const groupedAttributes = useMemo(() => {
        if (!product.custom_attributes || product.custom_attributes.length === 0) {
            return {};
        }

        const groups = {};
        product.custom_attributes.forEach(attr => {
            const categoryName = attr.category_name || 'Sin Categoria';
            if (!groups[categoryName]) {
                groups[categoryName] = {
                    category_id: attr.category_id,
                    category_name: categoryName,
                    attributes: []
                };
            }
            groups[categoryName].attributes.push(attr);
        });

        return groups;
    }, [product.custom_attributes]);

    const categoryLimits = useMemo(() => {
        return product.category_limits || {};
    }, [product.category_limits]);

    const calculateCategoryTotal = useCallback((categoryId) => {
        if (!product.custom_attributes) return 0;
        let total = 0;
        product.custom_attributes.forEach(attr => {
            if (attr.category_id === categoryId) {
                total += (selectedQuantities[attr.id] || 0);
            }
        });
        return total;
    }, [selectedQuantities, product.custom_attributes]);

    const getCategoryMinLimit = useCallback((categoryId) => {
        return categoryLimits[categoryId]?.min_quantity || 0;
    }, [categoryLimits]);

    const getCategoryMaxLimit = useCallback((categoryId) => {
        return categoryLimits[categoryId]?.max_quantity || 0;
    }, [categoryLimits]);

    const attrExtrasPrice = useMemo(() => {
        let extras = 0;
        if (product.custom_attributes && product.custom_attributes.length > 0) {
            product.custom_attributes.forEach(attr => {
                const qty = selectedQuantities[attr.id] || 0;
                const defaultQty = attr.is_default ? attr.default_qty : 0;
                const extraQty = Math.max(0, qty - defaultQty);
                if (extraQty > 0) {
                    extras += attr.price * extraQty;
                }
            });
        }
        return extras;
    }, [selectedQuantities, product]);

    const groupedDrinkAttributes = useMemo(() => {
        if (!selectedDrink?.drink?.custom_attributes?.length) return {};
        const groups = {};
        selectedDrink.drink.custom_attributes.forEach(attr => {
            const cat = attr.category_name || 'Sin Categoría';
            if (!groups[cat]) {
                groups[cat] = { category_id: attr.category_id, category_name: cat, attributes: [] };
            }
            groups[cat].attributes.push(attr);
        });
        return groups;
    }, [selectedDrink]);

    const drinkCategoryLimits = useMemo(() => {
        return selectedDrink?.drink?.category_limits || {};
    }, [selectedDrink]);

    const drinkAttrExtrasPrice = useMemo(() => {
        if (!selectedDrink?.drink?.custom_attributes?.length) return 0;
        let extras = 0;
        selectedDrink.drink.custom_attributes.forEach(attr => {
            const qty = drinkAttributeQuantities[attr.id] || 0;
            const defaultQty = attr.is_default ? attr.default_qty : 0;
            const extraQty = Math.max(0, qty - defaultQty);
            if (extraQty > 0) extras += attr.price * extraQty;
        });
        return extras;
    }, [selectedDrink, drinkAttributeQuantities]);

    const totalPrice = useMemo(() => {
        const base = selectedDrink ? selectedDrink.combo_price : product.price;
        return base + attrExtrasPrice + drinkAttrExtrasPrice;
    }, [selectedDrink, product.price, attrExtrasPrice, drinkAttrExtrasPrice]);

    const comboPriceSavings = useMemo(() => {
        if (!selectedDrink) return 0;
        return (product.price + selectedDrink.drink.price) - selectedDrink.combo_price;
    }, [selectedDrink, product.price]);

    // Fetch combo groups when modal opens (non-combo products only)
    useEffect(() => {
        if (product.id && !isCombo) {
            getComboGroups(product.id).then(result => {
                if (result.has_combos && result.combo_groups.length > 0) {
                    setComboGroups(result.combo_groups);
                    setStep('combo');
                } else {
                    setComboGroups([]);
                    setStep('detail');
                }
            });
        } else {
            setComboGroups([]);
            setStep('detail');
        }
        setSelectedDrink(null);
        setSelectedComboGroup(null);
    }, [product.id, isCombo, getComboGroups]);

    useEffect(() => {
        const initialQuantities = {};
        const initialExpanded = {};

        if (product.custom_attributes && product.custom_attributes.length > 0) {
            product.custom_attributes.forEach(attr => {
                if (attr.is_default && attr.default_qty > 0) {
                    initialQuantities[attr.id] = attr.default_qty;
                } else {
                    initialQuantities[attr.id] = 0;
                }
            });
        }

        Object.keys(groupedAttributes).forEach(categoryName => {
            const categoryData = groupedAttributes[categoryName];
            if (categoryData.attributes.length > 1) {
                initialExpanded[categoryName] = false;
            }
        });

        setSelectedQuantities(initialQuantities);
        setExpandedSections(initialExpanded);
        setCategoryErrors({});

        if (isCombo && product.comboProducts?.length > 0) {
            const tryFetchComboBanner = async () => {
                const candidates = product.comboProducts.slice(0, 2);
                for (const prod of candidates) {
                    const pid = prod.product_id || prod.id;
                    if (!pid) continue;
                    if (bannerCache.has(pid)) {
                        setBannerSrc(bannerCache.get(pid));
                        return;
                    }
                    try {
                        const r = await fetch(`${PROXY_URL}/api/products/${pid}/banner`);
                        const data = await r.json();
                        if (data.success && data.banner_base64) {
                            const src = `data:image/png;base64,${data.banner_base64}`;
                            bannerCache.set(pid, src);
                            setBannerSrc(src);
                            return;
                        }
                    } catch { }
                }
                setBannerSrc(null);
            };
            tryFetchComboBanner();
        } else if (isCombo) {
            setBannerSrc(null);
        } else if (product.id) {
            if (bannerCache.has(product.id)) {
                setBannerSrc(bannerCache.get(product.id));
            } else {
                setBannerSrc(null);
                fetch(`${PROXY_URL}/api/products/${product.id}/banner`)
                    .then(r => r.json())
                    .then(data => {
                        if (data.success && data.banner_base64) {
                            const src = `data:image/png;base64,${data.banner_base64}`;
                            bannerCache.set(product.id, src);
                            setBannerSrc(src);
                        }
                    })
                    .catch(() => { });
            }
        } else {
            setBannerSrc(null);
        }
    }, [product, groupedAttributes, isCombo]);

    const toggleSection = useCallback((categoryName) => {
        setExpandedSections(prev => ({
            ...prev,
            [categoryName]: !prev[categoryName]
        }));
    }, []);

    const handleSelectSingle = useCallback((attrId, _categoryId, categoryData) => {
        setSelectedQuantities(prev => {
            const newQuantities = { ...prev };
            categoryData.attributes.forEach(attr => {
                newQuantities[attr.id] = 0;
            });
            newQuantities[attrId] = 1;
            return newQuantities;
        });
        setCategoryErrors({});
    }, []);

    const handleIncrement = useCallback((attrId, maxQty, categoryId) => {
        setSelectedQuantities(prev => {
            const currentQty = prev[attrId] || 0;
            if (maxQty > 0 && currentQty >= maxQty) return prev;

            const categoryLimit = getCategoryMaxLimit(categoryId);
            if (categoryLimit > 0) {
                const currentCategoryTotal = calculateCategoryTotal(categoryId);
                if (currentCategoryTotal >= categoryLimit) return prev;
            }

            return {
                ...prev,
                [attrId]: currentQty + 1
            };
        });
    }, [getCategoryMaxLimit, calculateCategoryTotal]);

    const handleDecrement = useCallback((attrId, minQty) => {
        setSelectedQuantities(prev => {
            const currentQty = prev[attrId] || 0;
            if (currentQty <= 0) return prev;
            const newQty = currentQty - 1;
            if (newQty < minQty) return prev;
            return {
                ...prev,
                [attrId]: newQty
            };
        });
    }, []);

    const validateAllCategories = useCallback(() => {
        const errors = {};
        let hasErrors = false;

        Object.keys(groupedAttributes).forEach(categoryName => {
            const categoryData = groupedAttributes[categoryName];
            const categoryTotal = calculateCategoryTotal(categoryData.category_id);
            const minRequired = getCategoryMinLimit(categoryData.category_id);

            if (minRequired > 0 && categoryTotal < minRequired) {
                errors[categoryData.category_id] = `Selecciona al menos ${minRequired}`;
                hasErrors = true;
            }
        });

        setCategoryErrors(errors);
        return !hasErrors;
    }, [groupedAttributes, calculateCategoryTotal, getCategoryMinLimit]);

    const validateDrinkAttributes = useCallback(() => {
        if (!Object.keys(groupedDrinkAttributes).length) return true;
        const errors = {};
        let hasErrors = false;
        Object.values(groupedDrinkAttributes).forEach(catData => {
            const minRequired = drinkCategoryLimits[catData.category_id]?.min_quantity || 0;
            if (minRequired > 0) {
                const total = catData.attributes.reduce(
                    (sum, a) => sum + (drinkAttributeQuantities[a.id] || 0), 0
                );
                if (total < minRequired) {
                    errors[catData.category_id] = `Selecciona al menos ${minRequired}`;
                    hasErrors = true;
                }
            }
        });
        setDrinkCategoryErrors(errors);
        return !hasErrors;
    }, [groupedDrinkAttributes, drinkCategoryLimits, drinkAttributeQuantities]);

    const handleAddToCart = useCallback(() => {
        if (!validateAllCategories()) return;
        if (!validateDrinkAttributes()) return;

        const attributesDescription = [];
        const attribute_lines = [];

        if (product.custom_attributes && product.custom_attributes.length > 0) {
            product.custom_attributes.forEach(attr => {
                const qty = selectedQuantities[attr.id] || 0;
                if (qty > 0) {
                    attributesDescription.push(`${attr.name} x${qty}`);
                    attribute_lines.push({
                        attribute_id: attr.id,
                        name: attr.name,
                        attribute_name: attr.category_name || '',
                        category_id: attr.category_id || null,
                        qty: qty,
                        price: attr.price || 0,
                        is_addition: attr.price > 0
                    });
                }
            });
        }

        // atributos de la bebida seleccionada
        const drinkAttrLines = [];
        if (selectedDrink?.drink?.custom_attributes?.length) {
            selectedDrink.drink.custom_attributes.forEach(attr => {
                const qty = drinkAttributeQuantities[attr.id] || 0;
                if (qty > 0) {
                    drinkAttrLines.push({
                        attribute_id: attr.id,
                        name: attr.name,
                        attribute_name: attr.category_name || '',
                        category_id: attr.category_id || null,
                        qty,
                        price: attr.price || 0,
                        is_addition: attr.price > 0,
                        is_drink_attribute: true
                    });
                }
            });
        }

        const drinkWithAttrs = selectedDrink
            ? { ...selectedDrink, drink: { ...selectedDrink.drink, attribute_lines: drinkAttrLines } }
            : null;

        onSubmit(selectedQuantities, totalPrice, attributesDescription.join(', '), attribute_lines, drinkWithAttrs);
        onClose();
    }, [validateAllCategories, validateDrinkAttributes, product, selectedQuantities, totalPrice, onSubmit, onClose, selectedDrink, drinkAttributeQuantities]);

    const getMimeType = (b64) => b64?.startsWith('iVBOR') ? 'image/png' : 'image/jpeg';

    const getImageSrc = () => {
        if (product.image_base64) {
            return `data:${getMimeType(product.image_base64)};base64,${product.image_base64}`;
        }
        if (product.id) {
            return `${PROXY_URL}/images/products/${product.id}`;
        }
        return null;
    };

    const getSelectedLabel = (categoryData) => {
        const selectedItems = [];
        categoryData.attributes.forEach(attr => {
            const qty = selectedQuantities[attr.id] || 0;
            if (qty > 0) {
                selectedItems.push(`${attr.name}${qty > 1 ? ` x${qty}` : ''}`);
            }
        });
        return selectedItems.length > 0 ? selectedItems.join(', ') : 'Seleccionar';
    };

    const renderAttributeSection = (categoryName, categoryData) => {
        const isSingleOption = categoryData.attributes.length === 1;
        const minRequired = getCategoryMinLimit(categoryData.category_id);
        const maxLimit = getCategoryMaxLimit(categoryData.category_id);
        const isSingleSelection = maxLimit === 1;
        const isExpanded = expandedSections[categoryName];
        const hasError = categoryErrors[categoryData.category_id];

        if (isSingleOption) {
            const attr = categoryData.attributes[0];
            const qty = selectedQuantities[attr.id] || 0;

            return (
                <div key={categoryName} className="attribute-row-direct">
                    <div className="attribute-row-info">
                        <div className="attribute-row-name">{attr.name}</div>
                        {attr.price > 0 && (
                            <div className="attribute-row-price">${attr.price.toLocaleString('es-CL')} por porción extra</div>
                        )}
                    </div>
                    <div className="quantity-controls-inline">
                        <button
                            className="qty-btn-inline minus"
                            onClick={() => handleDecrement(attr.id, attr.min_qty)}
                            disabled={qty <= 0}
                        >
                            −
                        </button>
                        <div className="qty-display-inline">{qty}</div>
                        <button
                            className="qty-btn-inline plus"
                            onClick={() => handleIncrement(attr.id, attr.max_qty, categoryData.category_id)}
                        >
                            +
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div key={categoryName} className="accordion-section">
                <button
                    className={`accordion-header ${hasError ? 'error' : ''}`}
                    onClick={() => toggleSection(categoryName)}
                >
                    <div className="accordion-header-content">
                        <span className="accordion-title">
                            {categoryName}
                            {minRequired > 0 && <span className="required-indicator">*</span>}
                        </span>
                        <span className="accordion-selected">{getSelectedLabel(categoryData)}</span>
                    </div>
                    <svg
                        className={`accordion-icon ${isExpanded ? 'expanded' : ''}`}
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <path
                            d="M6 9L12 15L18 9"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

                {hasError && (
                    <div className="error-banner">{hasError}</div>
                )}

                <AnimatePresence initial={false}>
                {isExpanded && (
                    <motion.div
                        className="accordion-content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                        style={{ overflow: 'hidden' }}
                    >
                        {isSingleSelection ? (
                            <div className="options-list">
                                {categoryData.attributes.map(attr => {
                                    const qty = selectedQuantities[attr.id] || 0;
                                    const isSelected = qty > 0;

                                    return (
                                        <button
                                            key={attr.id}
                                            className={`option-item ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handleSelectSingle(attr.id, categoryData.category_id, categoryData)}
                                        >
                                            <div className="option-checkbox">
                                                {isSelected && (
                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M16.6667 5L7.5 14.1667L3.33334 10" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className="option-name">{attr.name}</span>
                                            {attr.price > 0 && (
                                                <span className="option-price">+${attr.price.toLocaleString()}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="options-list">
                                {categoryData.attributes.map(attr => {
                                    const qty = selectedQuantities[attr.id] || 0;

                                    return (
                                        <div key={attr.id} className="option-item-counter">
                                            <div className="option-item-info">
                                                <span className="option-name">{attr.name}</span>
                                                {attr.price > 0 && (
                                                    <span className="option-price-sub">${attr.price.toLocaleString('es-CL')} por porción extra</span>
                                                )}
                                            </div>
                                            <div className="quantity-controls-inline">
                                                <button
                                                    className="qty-btn-inline minus"
                                                    onClick={() => handleDecrement(attr.id, attr.min_qty)}
                                                    disabled={qty <= 0}
                                                >
                                                    −
                                                </button>
                                                <div className="qty-display-inline">{qty}</div>
                                                <button
                                                    className="qty-btn-inline plus"
                                                    onClick={() => handleIncrement(attr.id, attr.max_qty, categoryData.category_id)}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </motion.div>
                )}
                </AnimatePresence>
            </div>
        );
    };

    const renderDrinkAttributeSection = (categoryName, categoryData) => {
        const isSingleOption = categoryData.attributes.length === 1;
        const maxLimit = drinkCategoryLimits[categoryData.category_id]?.max_quantity || 0;
        const minRequired = drinkCategoryLimits[categoryData.category_id]?.min_quantity || 0;
        const isSingleSelection = maxLimit === 1;
        const isExpanded = drinkAttrExpanded[categoryName] ?? false;
        const hasError = drinkCategoryErrors[categoryData.category_id];

        const getDrinkSelectedLabel = () => {
            const selected = categoryData.attributes
                .filter(a => (drinkAttributeQuantities[a.id] || 0) > 0)
                .map(a => {
                    const qty = drinkAttributeQuantities[a.id];
                    return `${a.name}${qty > 1 ? ` x${qty}` : ''}`;
                });
            return selected.length > 0 ? selected.join(', ') : 'Seleccionar';
        };

        if (isSingleOption) {
            const attr = categoryData.attributes[0];
            const qty = drinkAttributeQuantities[attr.id] || 0;
            return (
                <div key={categoryName} className="attribute-row-direct">
                    <div className="attribute-row-info">
                        <div className="attribute-row-name">{attr.name}</div>
                        {attr.price > 0 && (
                            <div className="attribute-row-price">${attr.price.toLocaleString('es-CL')} por porción extra</div>
                        )}
                    </div>
                    <div className="quantity-controls-inline">
                        <button className="qty-btn-inline minus" onClick={() => handleDrinkDecrement(attr.id, attr.min_qty)} disabled={qty <= 0}>−</button>
                        <div className="qty-display-inline">{qty}</div>
                        <button className="qty-btn-inline plus" onClick={() => handleDrinkIncrement(attr.id, attr.max_qty, categoryData.category_id)}>+</button>
                    </div>
                </div>
            );
        }

        return (
            <div key={categoryName} className="accordion-section">
                <button
                    className={`accordion-header ${hasError ? 'error' : ''}`}
                    onClick={() => setDrinkAttrExpanded(prev => ({ ...prev, [categoryName]: !prev[categoryName] }))}
                >
                    <div className="accordion-header-content">
                        <span className="accordion-title">
                            {categoryName}
                            {minRequired > 0 && <span className="required-indicator">*</span>}
                        </span>
                        <span className="accordion-selected">{getDrinkSelectedLabel()}</span>
                    </div>
                    <svg className={`accordion-icon ${isExpanded ? 'expanded' : ''}`} width="32" height="32" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                {hasError && <div className="error-banner">{hasError}</div>}

                <AnimatePresence initial={false}>
                {isExpanded && (
                    <motion.div
                        className="accordion-content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                        style={{ overflow: 'hidden' }}
                    >
                        {isSingleSelection ? (
                            <div className="options-list">
                                {categoryData.attributes.map(attr => {
                                    const isSelected = (drinkAttributeQuantities[attr.id] || 0) > 0;
                                    return (
                                        <button
                                            key={attr.id}
                                            className={`option-item ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handleDrinkSelectSingle(attr.id, categoryData)}
                                        >
                                            <div className="option-checkbox">
                                                {isSelected && (
                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M16.6667 5L7.5 14.1667L3.33334 10" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className="option-name">{attr.name}</span>
                                            {attr.price > 0 && <span className="option-price">+${attr.price.toLocaleString()}</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="options-list">
                                {categoryData.attributes.map(attr => {
                                    const qty = drinkAttributeQuantities[attr.id] || 0;
                                    return (
                                        <div key={attr.id} className="option-item-counter">
                                            <div className="option-item-info">
                                                <span className="option-name">{attr.name}</span>
                                                {attr.price > 0 && <span className="option-price-sub">${attr.price.toLocaleString('es-CL')} por porción extra</span>}
                                            </div>
                                            <div className="quantity-controls-inline">
                                                <button className="qty-btn-inline minus" onClick={() => handleDrinkDecrement(attr.id, attr.min_qty)} disabled={qty <= 0}>−</button>
                                                <div className="qty-display-inline">{qty}</div>
                                                <button className="qty-btn-inline plus" onClick={() => handleDrinkIncrement(attr.id, attr.max_qty, categoryData.category_id)}>+</button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </motion.div>
                )}
                </AnimatePresence>
            </div>
        );
    };

    const getMimeTypeGroup = (b64) => b64?.startsWith('iVBOR') ? 'image/png' : 'image/jpeg';

    const getGroupImageSrc = (b64, id, type) => {
        if (b64) return `data:${getMimeTypeGroup(b64)};base64,${b64}`;
        if (id) return `${PROXY_URL}/images/${type}/${id}`;
        return null;
    };

    const handleGroupClick = useCallback((group) => {
        setSelectedComboGroup(group);
        setSelectedDrink(null);
        setDrinkAttributeQuantities({});
        setDrinkAttrExpanded({});
        setDrinkCategoryErrors({});
        setStep('detail');
    }, []);

    const handleSelectDrink = useCallback((drink, comboGroup) => {
        setSelectedDrink({
            comboLineId: comboGroup.combo_line_id,
            combo_price: comboGroup.combo_price,
            drink
        });
        setDrinkCategoryErrors({});

        if (drink.custom_attributes?.length > 0) {
            const initQtys = {};
            const initExpanded = {};
            drink.custom_attributes.forEach(attr => {
                initQtys[attr.id] = attr.is_default && attr.default_qty > 0 ? attr.default_qty : 0;
            });
            const firstCat = drink.custom_attributes[0]?.category_name;
            if (firstCat) initExpanded[firstCat] = true;
            setDrinkAttributeQuantities(initQtys);
            setDrinkAttrExpanded(initExpanded);
        } else {
            setDrinkAttributeQuantities({});
            setDrinkAttrExpanded({});
        }
    }, []);

    const handleDrinkSelectSingle = useCallback((attrId, categoryData) => {
        setDrinkAttributeQuantities(prev => {
            const next = { ...prev };
            categoryData.attributes.forEach(a => { next[a.id] = 0; });
            next[attrId] = 1;
            return next;
        });
        setDrinkCategoryErrors({});
    }, []);

    const handleDrinkIncrement = useCallback((attrId, maxQty, categoryId) => {
        setDrinkAttributeQuantities(prev => {
            const qty = prev[attrId] || 0;
            if (maxQty > 0 && qty >= maxQty) return prev;
            const catLimit = drinkCategoryLimits[categoryId]?.max_quantity || 0;
            if (catLimit > 0) {
                const catTotal = Object.entries(prev)
                    .filter(([id]) => selectedDrink?.drink?.custom_attributes?.find(
                        a => a.id === parseInt(id) && a.category_id === categoryId
                    ))
                    .reduce((sum, [, q]) => sum + q, 0);
                if (catTotal >= catLimit) return prev;
            }
            return { ...prev, [attrId]: qty + 1 };
        });
    }, [drinkCategoryLimits, selectedDrink]);

    const handleDrinkDecrement = useCallback((attrId, minQty) => {
        setDrinkAttributeQuantities(prev => {
            const qty = prev[attrId] || 0;
            if (qty <= 0 || qty - 1 < minQty) return prev;
            return { ...prev, [attrId]: qty - 1 };
        });
    }, []);

    const renderComboStep = () => {
        const imageSrcCombo = getImageSrc();
        return (
            <div className="cs-container">
                {/* Header */}
                <div className="cs-header">
                    <img src={logo} alt="GetIT" className="cs-logo" />
                    <div className="cs-posgo-badge">
                        <img src={logoPosgo} alt="PosGo" className="cs-posgo-logo" />
                        <span className="cs-posgo-text">PosGo.cl</span>
                    </div>
                </div>

                {/* Body */}
                <div className="cs-body">
                    <div className="cs-scroll">
                        {/* Nombre categoría + producto */}
                        <div className="cs-product-title-block">
                            {product.category_name && (
                                <div className="cs-category-name">{product.category_name}</div>
                            )}
                            <div className="cs-product-name">{product.name}</div>
                        </div>

                        {/* Tarjeta producto — seleccionar solo */}
                        <div className="cs-product-card cs-product-card--solo" onClick={() => {
                            if (!product.custom_attributes || product.custom_attributes.length === 0) {
                                onClose();
                                onSubmit({}, product.price, '', [], null);
                            } else {
                                setStep('detail');
                            }
                        }}>
                            <div className="cs-product-price-badge">${product.price.toLocaleString('es-CL')}</div>
                            {imageSrcCombo
                                ? <img src={imageSrcCombo} alt={product.name} className="cs-product-img" />
                                : <div className="cs-product-img-placeholder">🍽️</div>
                            }
                            <div className="cs-product-card-name">{product.name}</div>
                        </div>

                        {/* Sección EN COMBO */}
                        <div className="cs-combo-section">
                        <div className="cs-combo-label">EN COMBO</div>
                        <div className="cs-groups-row">
                            {comboGroups.map(group => {
                                const groupImgSrc = getGroupImageSrc(group.drink_group.image_base64, group.drink_group.id, 'groups');

                                return (
                                    <button
                                        key={group.combo_line_id}
                                        className="cs-group-card"
                                        onClick={() => handleGroupClick(group)}
                                    >
                                        <div className="cs-card-price">${Math.round(group.combo_price).toLocaleString('es-CL')}</div>
                                        <div className="cs-card-images">
                                            {imageSrcCombo && (
                                                <img src={imageSrcCombo} alt={product.name} className="cs-card-product-img" />
                                            )}
                                            {groupImgSrc
                                                ? <img src={groupImgSrc} alt={group.drink_group.name} className="cs-card-group-img" />
                                                : <div className="cs-card-group-placeholder">🥤</div>
                                            }
                                        </div>
                                        <div className="cs-card-group-name">{group.drink_group.name}</div>
                                    </button>
                                );
                            })}
                        </div>
                        </div>{/* /cs-combo-section */}
                    </div>{/* /cs-scroll */}
                </div>{/* /cs-body */}

                {/* Footer */}
                <div className="cs-footer">
                    <button className="cs-btn-back" onClick={onClose}>
                        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{flexShrink:0}}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Volver al menú
                    </button>
                </div>

                <style>{`
                    .cs-container {
                        position: fixed;
                        top: 0; left: 0; right: 0; bottom: 0;
                        background: #f5f5f5;
                        z-index: 9999;
                        display: flex;
                        flex-direction: column;
                        overflow: hidden;
                    }
                    .cs-header {
                        background: white;
                        padding: 1rem 1.5rem;
                        flex-shrink: 0;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.07);
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        position: relative;
                    }
                    .cs-logo { height: 100px; width: auto; }
                    .cs-posgo-badge {
                        position: absolute;
                        right: 1.5rem;
                        display: flex;
                        align-items: center;
                        gap: 0.4rem;
                    }
                    .cs-posgo-logo { height: 80px; width: auto; }
                    .cs-posgo-text {
                        font-size: 1.5rem;
                        font-weight: 700;
                        color: #db2113;
                        font-family: Quicksand, sans-serif;
                    }
                    .cs-body {
                        flex: 1;
                        overflow: hidden;
                        display: flex;
                        flex-direction: column;
                    }
                    .cs-scroll {
                        flex: 1;
                        overflow-y: auto;
                        padding: 1rem 2rem;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: space-evenly;
                    }
                    .cs-product-title-block {
                        text-align: center;
                        width: 100%;
                    }
                    .cs-category-name {
                        font-size: 3rem;
                        font-weight: 900;
                        color: #0E8164;
                        text-transform: uppercase;
                        letter-spacing: 0.03em;
                        line-height: 1.2;
                    }
                    .cs-product-name {
                        font-size: 3.2rem;
                        font-weight: 900;
                        color: #0E8164;
                        line-height: 1.2;
                    }
                    .cs-product-card {
                        background: white;
                        border-radius: 24px;
                        box-shadow: 0 2px 12px rgba(0,0,0,0.1);
                        padding: 2rem 2.25rem 1.5rem;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        gap: 0.75rem;
                        width: 100%;
                        max-width: 460px;
                        position: relative;
                    }
                    .cs-product-card--solo {
                        cursor: pointer;
                        border: 3px solid transparent;
                        transition: all 0.2s;
                    }
                    .cs-product-card--solo:hover {
                        border-color: #0E8164;
                        box-shadow: 0 8px 24px rgba(14,129,100,0.2);
                        transform: translateY(-3px);
                    }
                    .cs-product-price-badge {
                        position: absolute;
                        top: 1.25rem;
                        right: 1.5rem;
                        font-size: 2rem;
                        font-weight: 900;
                        color: #0E8164;
                    }
                    .cs-product-img {
                        width: 260px;
                        height: 260px;
                        object-fit: contain;
                    }
                    .cs-product-img-placeholder {
                        width: 260px;
                        height: 260px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 7rem;
                    }
                    .cs-product-card-name {
                        font-size: 1.5rem;
                        font-weight: 700;
                        color: #0E8164;
                        text-align: center;
                    }
                    .cs-combo-section {
                        width: 100%;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                    }
                    .cs-combo-label {
                        font-size: 2.6rem;
                        font-weight: 900;
                        color: #FF9500;
                        letter-spacing: 0.08em;
                        text-align: center;
                        margin-bottom: 1.25rem;
                    }
                    .cs-groups-row {
                        display: flex;
                        flex-direction: row;
                        flex-wrap: wrap;
                        gap: 0.75rem;
                        justify-content: center;
                        width: 100%;
                    }
                    .cs-group-wrap {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        gap: 0.75rem;
                    }
                    .cs-group-card {
                        background: white;
                        border: 3px solid #e5e7eb;
                        border-radius: 20px;
                        padding: 1rem 1rem 0.75rem;
                        width: 200px;
                        overflow: hidden;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        gap: 0.25rem;
                        cursor: pointer;
                        transition: all 0.2s;
                        position: relative;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                    }
                    .cs-group-card:hover {
                        border-color: #0E8164;
                        box-shadow: 0 6px 20px rgba(14,129,100,0.2);
                        transform: translateY(-3px);
                    }
                    .cs-group-card--expanded {
                        border-color: #0E8164;
                        background: #f0fdf4;
                    }
                    .cs-card-price {
                        position: absolute;
                        top: 1rem;
                        right: 1.25rem;
                        font-size: 1.4rem;
                        font-weight: 900;
                        color: #0E8164;
                    }
                    .cs-card-images {
                        position: relative;
                        width: 100%;
                        height: 120px;
                        margin-top: 0.75rem;
                        overflow: hidden;
                    }
                    .cs-card-product-img {
                        position: absolute;
                        left: 0;
                        bottom: 0;
                        width: 95px;
                        height: 95px;
                        object-fit: contain;
                        z-index: 1;
                    }
                    .cs-card-group-img {
                        position: absolute;
                        right: 0;
                        bottom: 0;
                        width: 90px;
                        height: 90px;
                        object-fit: contain;
                        z-index: 2;
                    }
                    .cs-card-group-placeholder {
                        position: absolute;
                        right: 0;
                        bottom: 0;
                        width: 90px;
                        height: 90px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 4rem;
                        z-index: 2;
                    }
                    .cs-card-group-name {
                        font-size: 1.25rem;
                        font-weight: 700;
                        color: #0E8164;
                        text-align: center;
                        line-height: 1.3;
                        padding-top: 0.5rem;
                    }
                    .cs-drinks-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
                        gap: 0.75rem;
                        width: 100%;
                        animation: csExpandDown 0.2s ease;
                    }
                    @keyframes csExpandDown {
                        from { opacity: 0; transform: translateY(-6px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .cs-drink-card {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        gap: 0.5rem;
                        padding: 1rem 0.75rem;
                        background: white;
                        border: 2px solid #e5e7eb;
                        border-radius: 14px;
                        cursor: pointer;
                        transition: all 0.2s;
                        text-align: center;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.05);
                    }
                    .cs-drink-card:hover {
                        border-color: #0E8164;
                        background: #f0fdf4;
                        transform: translateY(-2px);
                        box-shadow: 0 6px 16px rgba(14,129,100,0.15);
                    }
                    .cs-drink-img {
                        width: 70px;
                        height: 70px;
                        object-fit: contain;
                    }
                    .cs-drink-img-placeholder {
                        width: 70px;
                        height: 70px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 2rem;
                    }
                    .cs-drink-name {
                        font-size: 0.9rem;
                        font-weight: 700;
                        color: #1a1a1a;
                        line-height: 1.3;
                    }
                    .cs-drink-price {
                        font-size: 0.9rem;
                        font-weight: 700;
                        color: #6b7280;
                    }
                    .cs-footer {
                        background: white;
                        padding: 1.5rem 2rem;
                        box-shadow: 0 -4px 12px rgba(0,0,0,0.08);
                        border-top: 2px solid #e5e7eb;
                        flex-shrink: 0;
                        display: flex;
                        align-items: center;
                        min-height: 90px;
                    }
                    .cs-btn-back {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        padding: 1.1rem 2.5rem;
                        font-size: 1.3rem;
                        font-weight: 800;
                        border: none;
                        border-radius: 50px;
                        cursor: pointer;
                        background: #0E8164;
                        color: white;
                        transition: all 0.2s;
                        white-space: nowrap;
                    }
                    .cs-btn-back:hover { background: #0a6b52; }
                `}</style>
            </div>
        );
    };

    const imageSrc = getImageSrc();

    const fullscreenContent = (
        <div className="fullscreen-container">
            <div className="fullscreen-header">
                <img src={logo} alt="GetIT" className="header-logo" />
                <div className="posgo-header">
                    <img src={logoPosgo} alt="PosGo" className="posgo-header-logo" />
                    <span className="posgo-header-text">PosGo.cl</span>
                </div>
            </div>

            <div className="fullscreen-body">
                <div className="content-scroll">
                    {bannerSrc ? (
                        <div className="product-banner-strip">
                            <img src={bannerSrc} alt="Banner" className="product-banner-image" />
                            <div className="product-banner-overlay">
                                <div className="product-banner-overlay-inner">
                                    <div className="product-banner-text">
                                        <h3 className="product-title-banner">{product.name}</h3>
                                        <div className="product-price-banner">
                                            {isCombo ? (
                                                <>
                                                    <span className="price-strikethrough-banner">${product.regular_price?.toLocaleString()}</span>
                                                    <span className="price-combo-banner">${product.price.toLocaleString()} • Combo -{Math.round(product.discount_percent)}%</span>
                                                </>
                                            ) : (
                                                `$${product.price.toLocaleString()}`
                                            )}
                                        </div>
                                    </div>
                                    {imageSrc && (
                                        <img src={imageSrc} alt={product.name} className="product-banner-thumb" />
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="product-header-info">
                            <div className="product-text-info">
                                <h3 className="product-title-large">{product.name}</h3>
                                <div className="product-base-price">
                                    {isCombo ? (
                                        <>
                                            <span className="price-strikethrough">
                                                ${product.regular_price?.toLocaleString()}
                                            </span>
                                            <span className="price-combo">
                                                ${product.price.toLocaleString()} • Combo -{Math.round(product.discount_percent)}%
                                            </span>
                                        </>
                                    ) : (
                                        `$${product.price.toLocaleString()} • sin modificación`
                                    )}
                                </div>
                            </div>
                            {imageSrc && (
                                <img src={imageSrc} alt={product.name} className={isCombo ? "combo-own-image" : "product-image-bg"} />
                            )}
                            {isCombo && !imageSrc && product.comboProducts && (
                                <div className="combo-products-images">
                                    {product.comboProducts.map((prod, index) => {
                                        const img = prod.image_base64
                                            ? `data:${getMimeType(prod.image_base64)};base64,${prod.image_base64}`
                                            : `${PROXY_URL}/images/products/${prod.id}`;
                                        return (
                                            <img
                                                key={index}
                                                src={img}
                                                alt={prod.name}
                                                className="combo-product-thumb"
                                                style={{ zIndex: product.comboProducts.length - index }}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="attributes-container">
                        {Object.keys(groupedAttributes).map(categoryName =>
                            renderAttributeSection(categoryName, groupedAttributes[categoryName])
                        )}
                    </div>

                    {selectedComboGroup && (
                        <div className="gda-wrap">
                            <div className="gda-header">
                                <div className="gda-header-left">
                                    <span className="gda-label">Elige tu bebida</span>
                                    <span className="gda-group-name">{selectedComboGroup.drink_group.name}</span>
                                </div>
                                <div className="gda-header-right">
                                    <span className="gda-group-price">Combo ${Math.round(selectedComboGroup.combo_price).toLocaleString('es-CL')}</span>
                                    <button className="gda-change-btn" onClick={() => { setStep('combo'); setSelectedDrink(null); setSelectedComboGroup(null); setDrinkAttributeQuantities({}); setDrinkAttrExpanded({}); setDrinkCategoryErrors({}); }}>Cambiar</button>
                                </div>
                            </div>
                            <div className="options-list">
                                {selectedComboGroup.drink_group.products.map(drink => {
                                    const isSelected = selectedDrink?.drink?.id === drink.id;
                                    return (
                                        <button
                                            key={drink.id}
                                            className={`option-item ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handleSelectDrink(drink, selectedComboGroup)}
                                        >
                                            <div className="option-checkbox">
                                                {isSelected && (
                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M16.6667 5L7.5 14.1667L3.33334 10" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className="option-name">{drink.name}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {selectedDrink && Object.keys(groupedDrinkAttributes).length > 0 && (
                                <div className="gda-attrs-wrap">
                                    <div className="gda-attrs-title">
                                        Opciones de {selectedDrink.drink.name}
                                    </div>
                                    <div className="gda-attrs-list">
                                        {Object.keys(groupedDrinkAttributes).map(catName =>
                                            renderDrinkAttributeSection(catName, groupedDrinkAttributes[catName])
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="fullscreen-footer">
                <div className="footer-actions">
                    <button className="btn-cancel" onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        className="btn-add-to-cart"
                        onClick={handleAddToCart}
                        disabled={
                            (selectedComboGroup && !selectedDrink) ||
                            (selectedDrink && Object.keys(groupedDrinkAttributes).length > 0 &&
                                Object.values(groupedDrinkAttributes).some(catData => {
                                    const min = drinkCategoryLimits[catData.category_id]?.min_quantity || 0;
                                    if (min === 0) return false;
                                    const total = catData.attributes.reduce((s, a) => s + (drinkAttributeQuantities[a.id] || 0), 0);
                                    return total < min;
                                })
                            )
                        }
                        style={
                            (selectedComboGroup && !selectedDrink) ||
                            (selectedDrink && Object.keys(groupedDrinkAttributes).length > 0 &&
                                Object.values(groupedDrinkAttributes).some(catData => {
                                    const min = drinkCategoryLimits[catData.category_id]?.min_quantity || 0;
                                    if (min === 0) return false;
                                    const total = catData.attributes.reduce((s, a) => s + (drinkAttributeQuantities[a.id] || 0), 0);
                                    return total < min;
                                })
                            ) ? { opacity: 0.45, cursor: 'not-allowed' } : {}
                        }
                    >
                        {selectedComboGroup && !selectedDrink
                            ? 'Selecciona una bebida para continuar'
                            : selectedDrink && Object.keys(groupedDrinkAttributes).length > 0 &&
                              Object.values(groupedDrinkAttributes).some(catData => {
                                  const min = drinkCategoryLimits[catData.category_id]?.min_quantity || 0;
                                  if (min === 0) return false;
                                  const total = catData.attributes.reduce((s, a) => s + (drinkAttributeQuantities[a.id] || 0), 0);
                                  return total < min;
                              })
                              ? 'Selecciona las opciones de tu bebida'
                            : selectedDrink && comboPriceSavings > 0
                                ? `Agregar Combo • $${totalPrice.toLocaleString()} (ahorrás $${Math.round(comboPriceSavings).toLocaleString('es-CL')})`
                                : `Agregar al Carrito • $${totalPrice.toLocaleString()}`
                        }
                    </button>
                </div>
            </div>

            <style>{`
                .fullscreen-container {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: #ffffff;
                    z-index: 9999;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }

                .fullscreen-header {
                    background: white;
                    padding: 1.5rem;
                    flex-shrink: 0;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.07);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    position: relative;
                }

                .header-logo {
                    height: 100px;
                    width: auto;
                }

                .posgo-header {
                    position: absolute;
                    right: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                }

                .posgo-header-logo {
                    height: 80px;
                    width: auto;
                }

                .posgo-header-text {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #db2113;
                    font-family: Quicksand, sans-serif;
                }

                .fullscreen-body {
                    flex: 1;
                    overflow: hidden;
                    background: #f5f5f5;
                }

                .content-scroll {
                    height: 100%;
                    overflow-y: auto;
                    padding: 3rem;
                }

                .product-header-info {
                    max-width: 1000px;
                    margin: 0 auto 3rem;
                    background: white;
                    border-radius: 20px;
                    padding: 2.5rem;
                    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 2rem;
                    min-height: 180px;
                }

                .product-text-info {
                    flex: 1;
                }

                .product-title-large {
                    font-size: 2.5rem;
                    font-weight: 900;
                    color: #1a1a1a;
                    margin: 0 0 1rem 0;
                    line-height: 1.2;
                }

                .product-base-price {
                    font-size: 1.375rem;
                    color: #6b7280;
                    font-weight: 600;
                }

                .price-strikethrough {
                    text-decoration: line-through;
                    color: #9ca3af;
                    margin-right: 1rem;
                }

                .price-combo {
                    color: #10b981;
                    font-weight: bold;
                }

                .product-image-bg {
                    width: 200px;
                    height: 200px;
                    object-fit: cover;
                    border-radius: 16px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    flex-shrink: 0;
                }

                .combo-own-image {
                    max-width: 220px;
                    max-height: 220px;
                    width: auto;
                    height: auto;
                    object-fit: contain;
                    flex-shrink: 0;
                }

                .combo-products-images {
                    display: flex;
                    gap: 1rem;
                    position: relative;
                }

                .combo-product-thumb {
                    width: 100px;
                    height: 100px;
                    object-fit: cover;
                    border-radius: 12px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    border: 3px solid white;
                }

                .product-banner-strip {
                    max-width: 1000px;
                    margin: 0 auto 3rem;
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
                    position: relative;
                }

                .product-banner-image {
                    width: 100%;
                    display: block;
                    object-fit: cover;
                }

                .product-banner-overlay {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    padding: 2rem 2.5rem;
                    background: linear-gradient(transparent, rgba(0, 0, 0, 0.65));
                    display: flex;
                    align-items: center;
                }

                .product-banner-overlay-inner {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 1.5rem;
                    width: 100%;
                }

                .product-banner-text {
                    flex: 1;
                }

                .product-banner-thumb {
                    width: 160px;
                    height: 160px;
                    object-fit: contain;
                    flex-shrink: 0;
                    filter: drop-shadow(0 4px 12px rgba(0,0,0,0.4));
                }

                .product-title-banner {
                    font-size: 2.5rem;
                    font-weight: 900;
                    color: white;
                    margin: 0 0 0.5rem 0;
                    line-height: 1.2;
                    text-shadow: 0 1px 4px rgba(0,0,0,0.4);
                }

                .product-price-banner {
                    font-size: 1.375rem;
                    color: rgba(255, 255, 255, 0.9);
                    font-weight: 600;
                }

                .price-strikethrough-banner {
                    text-decoration: line-through;
                    color: rgba(255, 255, 255, 0.6);
                    margin-right: 1rem;
                }

                .price-combo-banner {
                    color: #6ee7b7;
                    font-weight: bold;
                }

                .attributes-container {
                    max-width: 1000px;
                    margin: 0 auto;
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                .attribute-row-direct {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1.75rem 2rem;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                }

                .attribute-row-info {
                    flex: 1;
                }

                .attribute-row-name {
                    font-size: 1.375rem;
                    font-weight: 700;
                    color: #1a1a1a;
                    margin-bottom: 0.25rem;
                }

                .attribute-row-price {
                    font-size: 1rem;
                    color: #6b7280;
                    font-weight: 600;
                }

                .accordion-section {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                    overflow: hidden;
                    border-left: 3px solid #e5e7eb;
                    transition: all 0.2s;
                }

                .accordion-section:hover {
                    border-left-color: #10a37f;
                }

                .accordion-header {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1.75rem 2rem;
                    background: white;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s;
                    text-align: left;
                }

                .accordion-header:hover {
                    background: #f0fdf4;
                    border-left: 4px solid #0E8164;
                }

                .accordion-header.error {
                    background: #fef2f2;
                }

                .accordion-header-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .accordion-title {
                    font-size: 1.375rem;
                    font-weight: 700;
                    color: #1a1a1a;
                }

                .required-indicator {
                    color: #DC2626;
                    margin-left: 0.25rem;
                }

                .accordion-selected {
                    font-size: 1.125rem;
                    color: #DC2626;
                    font-weight: 600;
                }

                .accordion-icon {
                    color: #6b7280;
                    transition: transform 0.2s;
                    flex-shrink: 0;
                }

                .accordion-icon.expanded {
                    transform: rotate(180deg);
                }

                .error-banner {
                    padding: 1rem 2rem;
                    background: #fef2f2;
                    color: #DC2626;
                    font-size: 1.125rem;
                    font-weight: 600;
                    border-top: 2px solid #fee2e2;
                }

                .accordion-content {
                    padding: 1.5rem 2rem 2rem;
                    border-top: 2px solid #f3f4f6;
                }
                }

                .options-list {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .option-item {
                    display: flex;
                    align-items: center;
                    gap: 1.25rem;
                    padding: 1.25rem 1.5rem;
                    background: #f9fafb;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.2s;
                    width: 100%;
                    text-align: left;
                }

                .option-item:hover {
                    border-color: #0E8164;
                    background: #f0fdf4;
                }

                .option-item.selected {
                    border-color: #0E8164;
                    background: #0E8164;
                }

                .option-checkbox {
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    border: 3px solid #d1d5db;
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    transition: all 0.2s;
                }

                .option-item.selected .option-checkbox {
                    border-color: #0E8164;
                    background: #0E8164;
                }

                .option-name {
                    flex: 1;
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #1a1a1a;
                }

                .option-item.selected .option-name {
                    color: white;
                }

                .option-price {
                    font-size: 1.125rem;
                    font-weight: 700;
                    color: #0E8164;
                }

                .option-item.selected .option-price {
                    color: white;
                }

                .option-item-counter {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1.25rem 1.5rem;
                    background: #f9fafb;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                }

                .option-item-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }

                .option-price-sub {
                    font-size: 1rem;
                    color: #6b7280;
                }

                .quantity-controls-inline {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .qty-btn-inline {
                    width: 48px;
                    height: 48px;
                    border-radius: 10px;
                    border: none;
                    background: #0E8164;
                    color: white;
                    font-size: 1.5rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .qty-btn-inline.minus {
                    background: #9ca3af;
                }

                .qty-btn-inline:hover:not(:disabled) {
                    transform: scale(1.1);
                }

                .qty-btn-inline:disabled {
                    background: #e5e7eb;
                    cursor: not-allowed;
                    opacity: 0.5;
                }

                .qty-display-inline {
                    min-width: 48px;
                    text-align: center;
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #1a1a1a;
                }

                .fullscreen-footer {
                    background: white;
                    padding: 1.5rem 2rem;
                    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08);
                    border-top: 2px solid #e5e7eb;
                    flex-shrink: 0;
                    min-height: 90px;
                    display: flex;
                    align-items: center;
                }

                .footer-actions {
                    display: flex;
                    gap: 1.5rem;
                    max-width: 1400px;
                    margin: 0 auto;
                    align-items: center;
                }

                .btn-cancel {
                    padding: 1.25rem 2.5rem;
                    font-size: 1.25rem;
                    font-weight: 700;
                    border: 3px solid #6b7280;
                    border-radius: 16px;
                    cursor: pointer;
                    transition: all 200ms ease;
                    background: white;
                    color: #6b7280;
                }

                .btn-cancel:hover {
                    background: #6b7280;
                    color: white;
                    transform: translateY(-2px);
                }

                .btn-add-to-cart {
                    flex: 1;
                    padding: 1.5rem 3rem;
                    font-size: 1.5rem;
                    font-weight: 800;
                    border: none;
                    border-radius: 16px;
                    cursor: pointer;
                    transition: all 200ms ease;
                    background: #FF9500;
                    color: white;
                    box-shadow: 0 4px 12px rgba(255, 149, 0, 0.3);
                }

                .btn-add-to-cart:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 20px rgba(255, 149, 0, 0.4);
                    background: #e68600;
                }

                /* ── Drink Picker ── */
                .drink-picker-wrapper {
                    max-width: 1000px;
                    margin: 1.5rem auto 0;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                    overflow: hidden;
                    border-left: 4px solid #FF9500;
                }

                .drink-picker-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1.25rem 1.75rem;
                    background: #fffbf5;
                    border-bottom: 2px solid #f3f4f6;
                }

                .drink-picker-title-row {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .drink-picker-icon {
                    font-size: 1.5rem;
                }

                .drink-picker-title {
                    font-size: 1.25rem;
                    font-weight: 800;
                    color: #1a1a1a;
                }

                .drink-savings-badge {
                    background: #16a34a;
                    color: white;
                    padding: 0.4rem 1rem;
                    border-radius: 20px;
                    font-size: 1rem;
                    font-weight: 700;
                    white-space: nowrap;
                }

                .drink-groups-list {
                    display: flex;
                    flex-direction: column;
                }

                .drink-group-accordion {
                    border-bottom: 1px solid #f3f4f6;
                }

                .drink-group-accordion:last-child {
                    border-bottom: none;
                }

                .drink-group-accordion.has-selection > .drink-group-header {
                    background: #f0fdf4;
                }

                .drink-group-header {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 1rem 1.5rem;
                    background: white;
                    border: none;
                    cursor: pointer;
                    transition: background 0.2s;
                    text-align: left;
                }

                .drink-group-header:hover {
                    background: #f9fafb;
                }

                .drink-group-img {
                    width: 56px;
                    height: 56px;
                    object-fit: contain;
                    border-radius: 10px;
                    flex-shrink: 0;
                }

                .drink-group-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 0.2rem;
                }

                .drink-group-name {
                    font-size: 1.2rem;
                    font-weight: 700;
                    color: #1a1a1a;
                }

                .drink-group-price {
                    font-size: 1rem;
                    color: #FF9500;
                    font-weight: 700;
                }

                .drink-group-selected-label {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #16a34a;
                    background: #dcfce7;
                    padding: 0.3rem 0.8rem;
                    border-radius: 12px;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .drink-products-list {
                    padding: 0.75rem 1.5rem 1.25rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                    background: #fafafa;
                    animation: expandDown 0.2s ease;
                }

                .drink-product-item {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 0.9rem 1.25rem;
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.2s;
                    text-align: left;
                    width: 100%;
                }

                .drink-product-item:hover {
                    border-color: #FF9500;
                    background: #fffbf5;
                }

                .drink-product-item.selected {
                    border-color: #16a34a;
                    background: #16a34a;
                }

                .drink-product-img {
                    width: 48px;
                    height: 48px;
                    object-fit: contain;
                    border-radius: 8px;
                    flex-shrink: 0;
                }

                .drink-product-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 0.2rem;
                }

                .drink-product-name {
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: #1a1a1a;
                }

                .drink-product-item.selected .drink-product-name,
                .drink-product-item.selected .drink-product-price {
                    color: white;
                }

                .drink-product-price {
                    font-size: 0.95rem;
                    color: #6b7280;
                    font-weight: 600;
                }

                .drink-check {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.25);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .gda-wrap {
                    max-width: 1000px;
                    margin: 1.5rem auto 0;
                    background: white;
                    border-radius: 16px;
                    border-left: 4px solid #0E8164;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.07);
                    overflow: hidden;
                }
                .gda-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1rem 1.5rem;
                    background: #f0fdf4;
                    border-bottom: 1px solid #d1fae5;
                    gap: 1rem;
                }
                .gda-header-left {
                    display: flex;
                    flex-direction: column;
                    gap: 0.1rem;
                }
                .gda-label {
                    font-size: 0.85rem;
                    font-weight: 700;
                    color: #0E8164;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                .gda-group-name {
                    font-size: 1.1rem;
                    font-weight: 800;
                    color: #1a1a1a;
                }
                .gda-header-right {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    flex-shrink: 0;
                }
                .gda-group-price {
                    font-size: 1rem;
                    font-weight: 700;
                    color: #FF9500;
                }
                .gda-change-btn {
                    padding: 0.4rem 1rem;
                    font-size: 0.9rem;
                    font-weight: 700;
                    border: 2px solid #d1fae5;
                    border-radius: 8px;
                    background: white;
                    color: #0E8164;
                    cursor: pointer;
                    transition: all 0.2s;
                    white-space: nowrap;
                }
                .gda-change-btn:hover { background: #0E8164; color: white; border-color: #0E8164; }
                .gda-wrap .options-list {
                    padding: 1.25rem 1.5rem;
                }
                .gda-attrs-wrap {
                    border-top: 1px solid #d1fae5;
                    margin: 0 1.5rem 1rem;
                    padding-top: 0.75rem;
                }
                .gda-attrs-title {
                    font-size: 0.78rem;
                    font-weight: 700;
                    color: #0E8164;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    margin-bottom: 0.5rem;
                }
                .gda-attrs-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.4rem;
                }
                .gda-attrs-list .accordion-section {
                    box-shadow: none;
                    border: 1px solid #e5e7eb;
                    border-left: 2px solid #0E8164;
                    border-radius: 10px;
                }
                .gda-attrs-list .accordion-header {
                    padding: 0.75rem 1rem;
                }
                .gda-attrs-list .accordion-title {
                    font-size: 1rem;
                }
                .gda-attrs-list .accordion-selected {
                    font-size: 0.9rem;
                }
                .gda-attrs-list .accordion-content {
                    padding: 0.75rem 1rem 1rem;
                }
                .gda-attrs-list .attribute-row-direct {
                    padding: 0.75rem 1rem;
                    box-shadow: none;
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                }
                .gda-attrs-list .attribute-row-name {
                    font-size: 1rem;
                }
                .gda-attrs-list .options-list {
                    gap: 1.4rem;
                }
                .gda-attrs-list .accordion-content .option-item,
                .gda-attrs-list .accordion-content .option-item-counter {
                    margin-bottom: 0.6rem;
                }
                .gda-attrs-list .option-item {
                    padding: 1rem 1.25rem;
                }
                .gda-attrs-list .option-name {
                    font-size: 1.15rem;
                }
                .gda-attrs-list .option-price {
                    font-size: 1rem;
                }
                .gda-attrs-list .option-checkbox {
                    width: 28px;
                    height: 28px;
                }
                .gda-attrs-list .option-item-counter {
                    padding: 1rem 1.25rem;
                }
                .gda-attrs-list .qty-btn-inline {
                    width: 40px;
                    height: 40px;
                    font-size: 1.3rem;
                }
                .gda-attrs-list .qty-display-inline {
                    font-size: 1.3rem;
                    min-width: 40px;
                }

                @media (max-width: 768px) {
                    .content-scroll {
                        padding: 1.5rem;
                    }

                    .footer-actions {
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .btn-cancel,
                    .btn-add-to-cart {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );

    return createPortal(step === 'combo' ? renderComboStep() : fullscreenContent, document.body);
};

export default ProductOptionsModal;